/* Dependencies */

// Models
import { OnboardingProps } from './Onboarding.model';

/**
 * Onboarding.
 * @class
 */
class Onboarding {
  resetContainerElement: HTMLElement;
  resetContainerTriggerElement: HTMLElement;
  loginContainerElement: HTMLElement;
  loginContainerTriggerElement: HTMLElement;
  liveRegionElement: HTMLElement;
  resetMessageElement: HTMLElement;

  constructor(props: OnboardingProps) {
    this.resetContainerElement = props.resetContainerElement;
    this.resetContainerTriggerElement = props.resetContainerTriggerElement;
    this.loginContainerElement = props.loginContainerElement;
    this.loginContainerTriggerElement = props.loginContainerTriggerElement;
    this.liveRegionElement = props.liveRegionElement;
    this.resetMessageElement = props.resetMessageElement;

    // Bind events
    this.bindEvents();

    // Check for initial success messaging
    this.handleResetSentSuccess();
  }

  /**
   * Show the reset container to the user.
   */
  showResetContainer(): void {
    // Hide Login Container
    this.loginContainerElement.classList.remove('tw-block');
    this.loginContainerElement.classList.add('tw-hidden');

    // Show Reset Container
    this.resetContainerElement.classList.add('tw-block');
    this.resetContainerElement.classList.remove('tw-hidden');

    // Update user with message
    this.updateLiveRegion('Now Showing Forgot Password Form');
  }

  /**
   * Handle Reset Sent Success
   */
  handleResetSentSuccess(): void {
    const formElement = this.resetContainerElement.querySelector('form');
    if (formElement.querySelector('[data-message-sent]')) {
      // Show the email sent container.
      this.resetMessageElement.classList.remove('tw-hidden');
      this.resetMessageElement.classList.add('tw-block');

      // Update the user.
      this.updateLiveRegion('Password reset email has been sent.');
    }
  }

  /**
   * Show the login container to the user.
   */
  showLoginContainer(): void {
    // Hide Reset Container
    this.resetContainerElement.classList.remove('tw-block');
    this.resetContainerElement.classList.add('tw-hidden');

    // Show Login Container
    this.loginContainerElement.classList.add('tw-block');
    this.loginContainerElement.classList.remove('tw-hidden');

    // Update user with message
    this.updateLiveRegion('Now Showing Log In Form');
  }

  /**
   * Update the Aria Live Region.
   * @param message - The accessible message to be rendered.
   */
  updateLiveRegion(message: string): void {
    this.liveRegionElement.textContent = message;

    // Reposition focus
    const mainContentElement: HTMLElement =
      document.querySelector('#MainContent');
    mainContentElement.focus();
  }

  /**
   * Bind the required events.
   */
  bindEvents(): void {
    this.loginContainerTriggerElement.addEventListener('click', (event) => {
      event.preventDefault();
      this.showLoginContainer();
    });

    this.resetContainerTriggerElement.addEventListener('click', (event) => {
      event.preventDefault();
      this.showResetContainer();
    });
  }
}

/**
 * Onboarding Initialization.
 */
export const onboardingInit = (): void => {
  // Retrieve the container.
  const onboardingContainer: HTMLElement =
    document.querySelector('[data-onboarding]');

  // Exit if no container is found.
  if (!onboardingContainer) return;

  // Retrieve the required elements.
  const resetContainerElement: HTMLElement = onboardingContainer.querySelector(
    '[data-onboarding-recovery]'
  );
  const resetContainerTriggerElement: HTMLElement =
    onboardingContainer.querySelector('[data-onboarding-recovery-trigger]');
  const loginContainerElement: HTMLElement = onboardingContainer.querySelector(
    '[data-onboarding-login]'
  );
  const loginContainerTriggerElement: HTMLElement =
    onboardingContainer.querySelector('[data-onboarding-login-trigger]');
  const liveRegionElement: HTMLElement = onboardingContainer.querySelector(
    '[data-onboarding-live-region]'
  );
  const resetMessageElement: HTMLElement = onboardingContainer.querySelector(
    '[data-reset-message]'
  );

  // Handle create flow
  if (!loginContainerElement) return;

  // Create a new onboarding instance.
  new Onboarding({
    resetContainerElement,
    resetContainerTriggerElement,
    loginContainerElement,
    loginContainerTriggerElement,
    liveRegionElement,
    resetMessageElement,
  });
};
